import { useTranslation } from 'react-i18next';
import {
    Button,
    Callout,
    Checkbox,
    FormField,
    ImportDropzone,
    Modal,
    PlainTable,
    Select,
} from '@/components/ui';
import {
    IMPORT_TARGETS,
    type ImportAccountsCsvVM,
    type ImportRowPlan,
    type ImportTarget,
} from './useImportAccountsCsv';
import type { ScopedAccountsVM } from './useScopedAccounts';
import styles from './ImportAccountsCsvModal.module.css';

const STEPS: ImportAccountsCsvVM['step'][] = ['upload', 'map', 'preview'];

/**
 * EAA-1839 (frame 35c): the three-step "Nhập tài khoản từ CSV" modal — tải tệp →
 * ghép cột → xem trước & xác nhận.
 *
 * See `useImportAccountsCsv` for what the apply step really does (a sequential,
 * cancellable run over the two single-account writes, capped at 200 rows), why a
 * run can stop half-done, and which three of the frame's options are rendered inert
 * and for what contract reason.
 */
export function ImportAccountsCsvModal({
    vm,
    importer,
}: {
    vm: ScopedAccountsVM;
    importer: ImportAccountsCsvVM;
}) {
    const { t } = useTranslation();

    const stepIndex = STEPS.indexOf(importer.step);

    let body: React.ReactNode = null;

    if (importer.step === 'upload') {
        body = (
            <>
                <ImportDropzone
                    accept=".csv,text/csv"
                    onFilesSelected={(files) => {
                        if (files[0]) void importer.acceptFile(files[0]);
                    }}
                    hint={t('console.hierarchy.scopedAccounts.import.dropHint')}
                    subHint={t('console.hierarchy.scopedAccounts.import.dropSubHint')}
                />
                {importer.parseError && (
                    <Callout tone="danger" longLabel>
                        {importer.parseError}
                    </Callout>
                )}
                <Callout tone="info" longLabel>
                    {t('console.hierarchy.scopedAccounts.import.scopeNote', {
                        org: vm.orgName ?? '',
                    })}
                </Callout>
            </>
        );
    }

    if (importer.step === 'map') {
        body = (
            <>
                <div className={styles.file}>
                    <strong>{importer.fileName}</strong>
                    <span className={styles.fileMeta}>
                        {t('console.hierarchy.scopedAccounts.import.fileMeta', {
                            rows: importer.plan.length,
                            columns: importer.headers.length,
                            kb: Math.max(1, Math.round(importer.fileSize / 1024)),
                        })}
                    </span>
                    <Button type="link" size="small" onClick={() => importer.setStep('upload')}>
                        {t('console.hierarchy.scopedAccounts.import.changeFile')}
                    </Button>
                </div>

                <PlainTable<{ index: number; header: string }>
                    className="sa-import__map"
                    columns={[
                        {
                            key: 'header',
                            label: t('console.hierarchy.scopedAccounts.import.columnInFile'),
                            render: (row) => <code>{row.header}</code>,
                        },
                        {
                            key: 'target',
                            label: t('console.hierarchy.scopedAccounts.import.columnInPortal'),
                            render: (row) => (
                                <Select<ImportTarget>
                                    size="small"
                                    value={importer.mapping[row.index]}
                                    onChange={(value) => importer.setColumnTarget(row.index, value)}
                                    options={IMPORT_TARGETS.map((target) => ({
                                        value: target,
                                        label: t(
                                            `console.hierarchy.scopedAccounts.import.target_${target}`
                                        ),
                                    }))}
                                />
                            ),
                        },
                    ]}
                    rows={importer.headers.map((header, index) => ({ index, header }))}
                    rowKey={(row) => String(row.index)}
                />

                <FormField
                    label={t('console.hierarchy.scopedAccounts.import.defaultLevelLabel')}
                    hint={t('console.hierarchy.scopedAccounts.import.defaultLevelHint')}
                >
                    <Select
                        value={importer.defaultLevelId}
                        onChange={(value) => importer.setDefaultLevelId(value as string)}
                        options={[
                            {
                                value: '',
                                label: t('console.hierarchy.scopedAccounts.import.defaultLevelNone'),
                            },
                            ...vm.activeLevels.map((level) => ({
                                value: level.id,
                                label: level.name,
                            })),
                        ]}
                    />
                </FormField>

                <Checkbox
                    checked={importer.sendInvite}
                    onChange={(event) => importer.setSendInvite(event.target.checked)}
                >
                    {t('console.hierarchy.scopedAccounts.import.sendInviteLabel')}
                </Checkbox>
                <Checkbox checked={false} disabled title={t('console.hierarchy.scopedAccounts.import.updateExistingReason')}>
                    {t('console.hierarchy.scopedAccounts.import.updateExistingLabel')}
                </Checkbox>
                <Checkbox checked={false} disabled title={t('console.hierarchy.scopedAccounts.import.deactivateMissingReason')}>
                    {t('console.hierarchy.scopedAccounts.import.deactivateMissingLabel')}
                </Checkbox>

                {importer.mappingError && (
                    <Callout tone="danger" longLabel>
                        {importer.mappingError}
                    </Callout>
                )}
            </>
        );
    }

    if (importer.step === 'preview') {
        body = (
            <>
                <Callout
                    tone={importer.rejectedCount > 0 ? 'warning' : 'info'}
                    longLabel
                >
                    {t('console.hierarchy.scopedAccounts.import.previewSummary', {
                        accepted: importer.acceptedRows.length,
                        rejected: importer.rejectedCount,
                    })}
                </Callout>

                {importer.overCap && (
                    <Callout tone="danger" longLabel>
                        {t('console.hierarchy.scopedAccounts.import.overCap', {
                            max: importer.maxRows,
                        })}
                    </Callout>
                )}

                <Callout tone="warning" longLabel>
                    {t('console.hierarchy.scopedAccounts.import.partialWarning')}
                </Callout>

                <PlainTable<ImportRowPlan>
                    className="sa-import__preview"
                    columns={[
                        {
                            key: 'lineNumber',
                            label: t('console.hierarchy.scopedAccounts.import.rowLine'),
                            render: (row) => row.lineNumber,
                        },
                        {
                            key: 'fullName',
                            label: t('console.hierarchy.scopedAccounts.import.rowName'),
                            render: (row) => row.fullName || '—',
                        },
                        {
                            key: 'username',
                            label: t('console.hierarchy.scopedAccounts.import.rowUsername'),
                            render: (row) => row.username || '—',
                        },
                        {
                            key: 'email',
                            label: t('console.hierarchy.scopedAccounts.import.rowEmail'),
                            render: (row) => row.email || '—',
                        },
                        {
                            key: 'levelName',
                            label: t('console.hierarchy.scopedAccounts.import.rowLevel'),
                            render: (row) =>
                                row.levelId
                                    ? row.levelName ||
                                      t('console.hierarchy.scopedAccounts.import.rowLevelDefault')
                                    : t('console.hierarchy.scopedAccounts.import.rowLevelNone'),
                        },
                        {
                            key: 'problems',
                            label: t('console.hierarchy.scopedAccounts.import.rowProblems'),
                            render: (row) =>
                                row.problems.length === 0 ? (
                                    <span className={styles.ok}>
                                        {t('console.hierarchy.scopedAccounts.import.rowReady')}
                                    </span>
                                ) : (
                                    <span className={styles.bad}>{row.problems.join(' · ')}</span>
                                ),
                        },
                    ]}
                    rows={importer.plan}
                    rowKey={(row) => String(row.lineNumber)}
                />

                {importer.results.length > 0 && (
                    <ul className={styles.results}>
                        {importer.results.map((result) => (
                            <li
                                key={result.lineNumber}
                                className={result.ok ? styles.ok : styles.bad}
                            >
                                {t('console.hierarchy.scopedAccounts.import.resultLine', {
                                    line: result.lineNumber,
                                    username: result.username,
                                    message: result.message,
                                })}
                            </li>
                        ))}
                    </ul>
                )}
            </>
        );
    }

    let footer: React.ReactNode = (
        <Button onClick={importer.closeModal}>{t('common.cancel')}</Button>
    );
    if (importer.step === 'map') {
        footer = (
            <>
                <Button onClick={() => importer.setStep('upload')}>
                    {t('console.hierarchy.scopedAccounts.import.backButton')}
                </Button>
                <Button
                    type="primary"
                    disabled={Boolean(importer.mappingError)}
                    onClick={() => importer.setStep('preview')}
                >
                    {t('console.hierarchy.scopedAccounts.import.previewButton', {
                        count: importer.plan.length,
                    })}
                </Button>
            </>
        );
    }
    if (importer.step === 'preview') {
        footer = (
            <>
                <Button onClick={() => importer.setStep('map')} disabled={importer.running}>
                    {t('console.hierarchy.scopedAccounts.import.backButton')}
                </Button>
                {importer.running ? (
                    <Button danger onClick={importer.cancelRun}>
                        {t('console.hierarchy.scopedAccounts.import.cancelRunButton')}
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        disabled={importer.acceptedRows.length === 0 || importer.overCap}
                        onClick={() => void importer.run()}
                    >
                        {t('console.hierarchy.scopedAccounts.import.applyButton', {
                            count: importer.acceptedRows.length,
                        })}
                    </Button>
                )}
            </>
        );
    }

    return (
        <Modal
            open={importer.open}
            onCancel={importer.closeModal}
            width={720}
            className="sa-import"
            title={t('console.hierarchy.scopedAccounts.import.title')}
            footer={footer}
        >
            <ol className={styles.steps}>
                {STEPS.map((step, index) => (
                    <li
                        key={step}
                        className={
                            styles.step +
                            (index === stepIndex ? ' is-current' : '') +
                            (index < stepIndex ? ' is-done' : '')
                        }
                    >
                        <span className={styles.stepIndex}>{index + 1}</span>
                        {t(`console.hierarchy.scopedAccounts.import.step_${step}`)}
                    </li>
                ))}
            </ol>
            {body}
        </Modal>
    );
}

export default ImportAccountsCsvModal;
